import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '~/lib/utils'

export interface ModalProps {
    open: boolean
    onClose: () => void
    title?: ReactNode
    children: ReactNode
    /** 카드 폭(기본 max-w-lg). */
    className?: string
}

/**
 * 공통 모달 셸 — 배경(backdrop) 클릭/ESC/X로 닫힘, 중앙 정렬, 내부 스크롤.
 * 손코딩 `fixed inset-0 ...` 오버레이 복붙 대신 이 컴포넌트를 쓴다.
 */
export function Modal({
    open,
    onClose,
    title,
    children,
    className,
}: ModalProps) {
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [open, onClose])

    if (!open) return null
    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
            onClick={onClose}>
            <div
                className={cn(
                    'border-border bg-card flex max-h-[85vh] w-full max-w-lg flex-col rounded-md border shadow-xl',
                    className
                )}
                onClick={(e) => e.stopPropagation()}
                role='dialog'
                aria-modal='true'>
                {title != null ? (
                    <div className='border-border flex items-center justify-between border-b px-5 py-3'>
                        <h2 className='text-foreground text-base font-semibold'>
                            {title}
                        </h2>
                        <button
                            type='button'
                            onClick={onClose}
                            className='text-muted-foreground hover:text-foreground'
                            aria-label='닫기'>
                            <X className='size-4' />
                        </button>
                    </div>
                ) : null}
                <div className='min-h-0 flex-1 overflow-y-auto p-5'>
                    {children}
                </div>
            </div>
        </div>
    )
}
